import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader, getToken, clearToken } from '../../../utils/auth';
import './DocumentExpiryBanner.css';

// P1-26: site-wide document expiry banner. Polls cheaply every 5 minutes
// against /api/documents/expiry-summary, dismissible per session.
const POLL_MS = 5 * 60 * 1000;
const SESSION_KEY = 'cs_doc_expiry_banner_dismissed';

/** Routes where the user is not authenticated — never call protected APIs. */
function isPublicAuthRoute(pathname) {
  const p = (pathname || '/').replace(/\/$/, '') || '/';
  return (
    p === '/' ||
    p === '/login' ||
    p === '/register' ||
    p === '/coming-soon' ||
    p.startsWith('/forgot-password') ||
    p.startsWith('/reset-password') ||
    p.startsWith('/verify/')
  );
}

const DocumentExpiryBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPublic = isPublicAuthRoute(location.pathname);
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem(SESSION_KEY) === '1'
  );

  const fetchSummary = useCallback(async () => {
    if (isPublic || !getToken()) {
      setSummary(null);
      return;
    }
    try {
      const r = await fetch(getApiEndpoint('/api/documents/expiry-summary'), { headers: getAuthHeader() });
      if (r.status === 401 || r.status === 403) {
        clearToken();
        setSummary(null);
        return;
      }
      if (!r.ok) return;
      const data = await r.json();
      setSummary({
        expired: Number(data?.expired || 0),
        expiring_soon: Number(data?.expiring_soon || 0),
      });
    } catch (_) { /* offline — silent */ }
  }, [isPublic]);

  useEffect(() => {
    if (isPublic) {
      setSummary(null);
      return undefined;
    }
    fetchSummary();
    const t = setInterval(fetchSummary, POLL_MS);
    // Refresh on tab focus so the banner reflects reality after the user
    // has been away — cheaper than upping the poll rate.
    const onFocus = () => fetchSummary();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [fetchSummary, isPublic]);

  const dismiss = (e) => {
    e?.stopPropagation();
    sessionStorage.setItem(SESSION_KEY, '1');
    setDismissed(true);
  };

  if (isPublic || dismissed || !summary) return null;
  const { expired, expiring_soon } = summary;
  if (expired === 0 && expiring_soon === 0) return null;

  const parts = [];
  if (expiring_soon > 0) parts.push(`${expiring_soon} expiring soon`);
  if (expired > 0) parts.push(`${expired} expired`);
  const tone = expired > 0 ? 'tone-red' : 'tone-amber';

  return (
    <div className={`cs-doc-expiry-banner ${tone}`} role="alert">
      <div className="cs-doc-expiry-banner-inner">
        <FaExclamationTriangle className="cs-doc-expiry-banner-icon" aria-hidden="true" />
        <span className="cs-doc-expiry-banner-text">
          {parts.join(' · ')}
          <span className="cs-doc-expiry-banner-text-suffix">
            {(expired + expiring_soon) === 1 ? ' document' : ' documents'} need attention
          </span>
        </span>
        <button
          type="button"
          className="cs-doc-expiry-banner-cta"
          onClick={() => navigate('/dashboard/documents?status=expiring')}
        >
          Review
        </button>
        <button
          type="button"
          className="cs-doc-expiry-banner-close"
          aria-label="Dismiss"
          onClick={dismiss}
        >
          <FaTimes />
        </button>
      </div>
    </div>
  );
};

export default DocumentExpiryBanner;
